<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfLivePreview from '@/components/Tools/PdfLivePreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfPage } from '@/composables/usePdfPage';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { merge as mergePdfPage } from '@/routes/tools/pdf';
import mergePdfTool from '@/routes/tools/pdf/merge';

const { maxMb } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.merge.seo',
    fallbackTitleKey: 'tools.pdf.merge.title',
    fallbackTitleLabel: 'Merge PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: mergePdfPage.url(),
});
const mergeDropSublabel = computed(
    () =>
        `${t('tools.pdf.ui.pdf_files_only', 'PDF files only')} · ${t('tools.dropzone.upload_limit_max', 'max')} ${maxMb.value} MB · ${t('tools.pdf.ui.merge_add_many', 'add as many as you need')}`,
);

const mergeFilesHint = computed(() => {
    const n = files.value.length;

    if (n <= 0) {
        return '';
    }

    if (n === 1) {
        return t(
            'tools.pdf.ui.merge_files_single',
            '1 file selected · Drag to reorder',
        );
    }

    return t(
        'tools.pdf.ui.merge_files_multi',
        '{n} files selected · Drag to reorder',
    ).replace('{n}', String(n));
});

const mergeButtonLabel = computed(() => {
    const n = files.value.length;

    if (n >= 2) {
        return t('tools.pdf.ui.merge_button_n', 'Merge {n} PDFs').replace(
            '{n}',
            String(n),
        );
    }

    return t('tools.pdf.ui.merge_button', 'Merge PDFs');
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.merge.title',
                title: 'Merge PDF',
                href: mergePdfPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const files = ref<File[]>([]);
const status = ref<Status>('idle');
const statusMessage = ref('');
const dragStartIndex = ref<number | null>(null);

const previewFileIndex = ref(0);
const previewPage = ref(1);
const pdfCanvas = ref<HTMLCanvasElement | null>(null);
const {
    isLoading: previewLoading,
    error: previewError,
    pageCount,
    previewZoomPct,
    loadFile,
    renderPage,
    unload,
} = usePdfPage();
const totalPreviewPages = computed(() => Math.max(1, pageCount.value));

const filesIdentity = computed(() =>
    files.value.map((f) => `${f.name}:${f.size}:${f.lastModified}`).join('>'),
);

async function loadPreviewForIndex(index: number): Promise<void> {
    const file = files.value[index];

    if (!file) {
        unload();

        return;
    }

    await loadFile(file);
    await nextTick();

    if (!pdfCanvas.value) {
        return;
    }

    previewPage.value = 1;
    await renderPage(pdfCanvas.value, 1, 420);
}

watch(filesIdentity, async () => {
    if (files.value.length === 0) {
        unload();
        previewPage.value = 1;

        return;
    }

    previewFileIndex.value = Math.min(
        previewFileIndex.value,
        files.value.length - 1,
    );
    await loadPreviewForIndex(previewFileIndex.value);
});

async function renderPreviewPage(page: number): Promise<void> {
    if (!pdfCanvas.value || files.value.length === 0) {
        return;
    }

    const clamped = Math.min(totalPreviewPages.value, Math.max(1, page));
    previewPage.value = clamped;
    await renderPage(pdfCanvas.value, clamped, 420);
}

watch(previewZoomPct, () => {
    if (files.value.length === 0 || !pdfCanvas.value) {
        return;
    }

    void renderPreviewPage(previewPage.value);
});

function onPreviewFileSelect(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);

    if (Number.isNaN(value) || value < 0 || value >= files.value.length) {
        return;
    }

    previewFileIndex.value = value;
    void loadPreviewForIndex(value);
}

function onFilesSelected(newFiles: File[]): void {
    files.value = [...files.value, ...newFiles];
    status.value = 'idle';
}

function removeFile(index: number): void {
    files.value.splice(index, 1);

    if (files.value.length === 0) {
        previewFileIndex.value = 0;
        unload();
        previewPage.value = 1;

        return;
    }

    if (previewFileIndex.value >= files.value.length) {
        previewFileIndex.value = files.value.length - 1;
    } else if (index < previewFileIndex.value) {
        previewFileIndex.value--;
    }
}

function onDragStart(index: number): void {
    dragStartIndex.value = index;
}

function onDragOver(event: DragEvent): void {
    event.preventDefault();
}

function onDrop(index: number): void {
    if (dragStartIndex.value === null || dragStartIndex.value === index) {
        dragStartIndex.value = null;

        return;
    }

    const copy = [...files.value];
    const [moved] = copy.splice(dragStartIndex.value, 1);
    copy.splice(index, 0, moved);
    files.value = copy;
    dragStartIndex.value = null;
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.merge_failed', 'Merge failed.')
            );
        } catch {
            return t('tools.pdf.ui.merge_failed', 'Merge failed.');
        }
    }

    return t('tools.pdf.ui.merge_failed', 'Merge failed.');
}

async function merge(): Promise<void> {
    if (files.value.length < 2 || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.merging', 'Merging PDFs...');

    const formData = new FormData();
    files.value.forEach((file) => formData.append('files[]', file));

    try {
        const response = await axios.post(mergePdfTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'merged.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.merged_success',
            'PDFs merged successfully!',
        );
        files.value = [];
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}

onUnmounted(unload);
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-merge" />

    <ToolLayout
        :title="t('tools.pdf.merge.title', 'Merge PDF')"
        :subtitle="t('tools.pdf.merge.description', '')"
        :show-how-to="false"
    >
        <div class="grid gap-4 lg:grid-cols-[2fr_3fr]">
            <!-- ── Left: preview ── -->
            <div class="lg:sticky lg:top-4 lg:self-start">
                <PdfLivePreview
                    v-model:zoom="previewZoomPct"
                    :has-file="files.length > 0"
                    :loading="previewLoading"
                    :error="previewError"
                    :current-page="previewPage"
                    :total-pages="pageCount"
                    :empty-message="
                        t(
                            'tools.pdf.ui.merge_empty_preview',
                            'Add PDFs to preview pages from your merge list.',
                        )
                    "
                    :rendering-message="
                        t('tools.pdf.ui.rendering', 'Rendering…')
                    "
                    @change-page="renderPreviewPage"
                >
                    <div class="flex justify-center px-2 py-4">
                        <canvas
                            ref="pdfCanvas"
                            class="max-h-none max-w-full rounded-md bg-white shadow-md dark:bg-gray-950"
                        />
                    </div>
                    <template #footer>
                        <div v-if="files.length > 1" class="mt-3 space-y-1">
                            <label
                                for="merge-preview-file"
                                class="block text-xs font-medium text-gray-600 dark:text-gray-400"
                            >
                                {{
                                    t(
                                        'tools.pdf.ui.merge_preview_document',
                                        'Preview document in merge order',
                                    )
                                }}
                            </label>
                            <select
                                id="merge-preview-file"
                                :value="previewFileIndex"
                                class="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                                @change="onPreviewFileSelect"
                            >
                                <option
                                    v-for="(file, index) in files"
                                    :key="`${file.name}-${index}`"
                                    :value="index"
                                >
                                    {{ index + 1 }}. {{ file.name }}
                                </option>
                            </select>
                        </div>
                        <p
                            v-if="files.length > 0 && !previewLoading"
                            class="mt-3 text-center text-xs text-gray-400 dark:text-gray-500"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.merge_preview_hint',
                                    'Page order in the preview follows each file; merged output uses the list order on the right.',
                                )
                            }}
                        </p>
                    </template>
                </PdfLivePreview>
                <div class="mt-4">
                    <ToolHowToCard />
                </div>
            </div>

            <!-- ── Right: controls ── -->
            <div class="space-y-4">
                <div
                    class="rounded-xl border border-border bg-card p-6 shadow-sm"
                >
                    <DropZone
                        accept="application/pdf"
                        :multiple="true"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdfs',
                                'Click or drag PDFs here',
                            )
                        "
                        :sublabel="mergeDropSublabel"
                        @update:model-value="onFilesSelected"
                    />

                    <div v-if="files.length > 0" class="mt-4 space-y-2">
                        <div
                            v-for="(file, index) in files"
                            :key="file.name + index"
                            draggable="true"
                            @dragstart="onDragStart(index)"
                            @dragover="onDragOver"
                            @drop.prevent="onDrop(index)"
                        >
                            <FileCard
                                :file="file"
                                :show-drag-handle="true"
                                @remove="removeFile(index)"
                            />
                        </div>
                    </div>

                    <p
                        v-if="files.length > 0"
                        class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                    >
                        {{ mergeFilesHint }}
                    </p>
                </div>

                <Button
                    class="w-full bg-brand-600 text-white hover:bg-brand-700"
                    :disabled="files.length < 2 || status === 'loading'"
                    @click="merge"
                >
                    {{ mergeButtonLabel }}
                </Button>

                <StatusMessage :status="status" :message="statusMessage" />
            </div>
        </div>
    </ToolLayout>
</template>
